/**
 * <SearchableSelect /> — single-value typeahead picker.
 *
 * Drop-in replacement for `<select>` when the option list is long enough
 * that scrolling is annoying — stadiums, teams, umpires, competitions.
 * For tiny static lists (gender, hand) the native select is fine; for
 * consistency the FormRenderer routes every "select" field through this
 * component.
 *
 * Behaviour:
 *  - Closed: shows the selected option's label, or the placeholder.
 *  - Click → opens a panel with a text input + filtered options.
 *  - Type → case-insensitive substring filter on labels.
 *  - Click option → set value, close, focus button.
 *  - "Clear" (✕) inline when a value is selected.
 *  - Outside-click closes.
 *  - Keyboard: ↑ / ↓ to highlight, Enter to pick, Esc to close.
 *
 * Same `{options, value, onChange, placeholder}` API as MultiSelect so
 * callers stay consistent.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X, Check, Search } from "lucide-react";

export default function SearchableSelect({
  options = [],          // [{ label, value }]
  value = "",            // string | null
  onChange,              // (next: string) => void
  placeholder = "Select…",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Auto-focus the search input when the panel opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      // next tick to ensure the input is in the DOM
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) =>
      String(o.label || "").toLowerCase().includes(q),
    );
  }, [options, query]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    // restore focus to the trigger so keyboard users can keep moving
    setTimeout(() => btnRef.current?.focus(), 0);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) pick(opt.value);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full h-[44px] py-2 pl-3 pr-9 rounded bg-input-secondary border border-gray-700 text-left text-sm focus:outline-none focus:border-button-primary truncate ${
          disabled ? "opacity-60 cursor-not-allowed" : ""
        }`}
      >
        {selected ? (
          <span className="text-text-primary">{selected.label}</span>
        ) : (
          <span className="text-text-secondary/70">{placeholder}</span>
        )}
      </button>

      {selected && !disabled && (
        <button
          type="button"
          onClick={clear}
          title="Clear selection"
          className="absolute right-8 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-bg-secondary text-text-secondary hover:text-text-primary"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}

      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none" />

      {open && (
        <div className="absolute left-0 right-0 mt-1 z-30 rounded-md border border-gray-700 bg-bg-primary shadow-2xl">
          <div className="relative p-2 border-b border-gray-800">
            <Search className="w-3.5 h-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={onKeyDown}
              placeholder="Type to filter…"
              className="w-full bg-input-secondary border border-gray-700 rounded pl-7 pr-2 py-1.5 text-sm focus:outline-none focus:border-button-primary"
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-text-secondary">
                No matches for “{query}”.
              </li>
            )}
            {filtered.map((opt, idx) => {
              const isSelected = String(opt.value) === String(value);
              const isHL = idx === highlight;
              return (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => { e.preventDefault(); pick(opt.value); }}
                  className={`px-3 py-1.5 text-sm cursor-pointer flex items-center justify-between ${
                    isHL ? "bg-bg-secondary text-text-primary" : "text-text-primary"
                  }`}
                >
                  <span className="truncate">{opt.label}</span>
                  {isSelected && <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 ml-2" />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
